import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, CircularProgress, Typography } from '@mui/material';

import launchDarklyToggles, {
  DARKLY_TOGGLE_NAMES,
} from '../../../main-deprecated/utils/launchDarklyToggles';
import WaeButton, { BUTTON_VARIANT } from '../../components/Button';
import JobOrderCard from '../../components/JobOrderCard';
import NoResultsCard from '../../components/NoResultsCard/NoResultsCard';
import ShiftCard from '../../components/ShiftCard/ShiftCard';
import { JOB_ORDER_STATUSES } from '../../constants/jobOrderConstants';
import selectUser from '../../store/selectors/appSelector';
import { BLACK, PRIMARY_COLOR } from '../../theme/colorConstants';
import { PRIMARY_FONT } from '../../theme/fontConstants';
import { getTimeStatus } from '../../utils/jobOrderHelpers';
import { ROUTE_CONSTANTS } from '../app/RouteBuilding/routeConstants';
import JobCreateTypeSelectModal from '../jobOrdersCreate/JobCreateTypeSelectModal';

import useInfiniteScroll from './hooks/useInfiniteScroll';
import JobOrdersFilters from './jobOrderFilters/JobOrdersFilters';
import buildApiFiltersFromSearchParams from './jobOrderFilters/query';
import DEFAULT_PAGE_SIZE, { MAX_SELECTED_JOB_ORDERS } from './constants';
import { getJobOrdersV2 } from './jobOrdersReducer';
import {
  selectJobOrdersV2,
  selectJobOrdersV2Cursor,
  selectJobOrdersV2GroupedByStart,
  selectJobOrdersV2HasMore,
  selectJobOrdersV2Loading,
  selectTimeCardIsUpdatingV2,
  selectTimecardUpdateErrorV2,
} from './jobOrdersSelectors';
import ManageAssociationsModal from './ManageAssociationsModal';
import { useJobOrdersV2Actions } from './useJobOrdersV2Actions';

export const JobOrdersV2 = ({ flags }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [searchParams, setSearchParams] = useSearchParams();
  const isLoading = useSelector(selectJobOrdersV2Loading);
  const jobOrders = useSelector(selectJobOrdersV2);
  const cursorFromStore = useSelector(selectJobOrdersV2Cursor);
  const hasMore = useSelector(selectJobOrdersV2HasMore);
  const timecardIsUpdating = useSelector(selectTimeCardIsUpdatingV2);
  const timecardUpdateError = useSelector(selectTimecardUpdateErrorV2);

  const isAdmin = user?.role === UserRole.ADMIN;
  const showCorporationFilter = isAdmin || user?.role === UserRole.RECRUITER;
  const canCreateJobOrder = isAdmin || user?.role === UserRole.EMPLOYER;
  const {
    clearTimeCardError,
    handleTimecardEditSubmit,
    onPlacementCheckStatusUpdate,
    onStatusUpdate,
  } = useJobOrdersV2Actions();

  const [isSelectJobCreateTypeModalOpen, setIsSelectJobCreateTypeModalOpen] = useState(false);
  const [isManageAssociationsModalOpen, setIsManageAssociationsModalOpen] = useState(false);
  const [selectedJobOrderIds, setSelectedJobOrderIds] = useState(() => new Set());

  const bulkDayJobEnabled = launchDarklyToggles(
    flags,
    DARKLY_TOGGLE_NAMES.isBulkDayJobAndSeriesCreationWorkflowEnabled
  );
  const alwaysShowRedesign = user?.role === UserRole.ADMIN || user?.role === UserRole.RECRUITER;
  const jobCardRedesignEnabled =
    alwaysShowRedesign || launchDarklyToggles(flags, DARKLY_TOGGLE_NAMES.hb5584JobCardRedesign);
  const JobCard = jobCardRedesignEnabled ? JobOrderCard : ShiftCard;

  const handleNewJobOrderClick = () => {
    if (bulkDayJobEnabled) {
      setIsSelectJobCreateTypeModalOpen(true);
      return;
    }
    navigate(ROUTE_CONSTANTS.JOB_ORDER_CREATE);
  };

  // Keep "now" stable while user is on this page (prevents default upcoming filter from changing every render).
  const nowMsRef = useRef(Date.now());

  // Cursor-pagination/sort will be wired to UI later; keep defaults here for payload shape.
  const [cursorToFetch, setCursorToFetch] = useState(null);
  const sortBy = useMemo(() => [{ field: 'start', descending: false }], []);

  // Reset pagination any time the URL filter query changes.
  const filterKey = useMemo(() => searchParams.toString(), [searchParams]);
  const previousFilterKeyRef = useRef(filterKey);
  const lastRequestedCursorKeyRef = useRef(null);
  const didFilterChange = previousFilterKeyRef.current !== filterKey;
  const effectiveCursor = didFilterChange ? null : cursorToFetch;
  useEffect(() => {
    if (didFilterChange) {
      setCursorToFetch(null);
      lastRequestedCursorKeyRef.current = null;
      setSelectedJobOrderIds(new Set());
    }
    previousFilterKeyRef.current = filterKey;
  }, [didFilterChange, filterKey]);

  const handleJobOrderSelectionToggle = useCallback((jobOrderId) => {
    setSelectedJobOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobOrderId)) {
        next.delete(jobOrderId);
        return next;
      }
      if (next.size >= MAX_SELECTED_JOB_ORDERS) {
        return prev;
      }
      next.add(jobOrderId);
      return next;
    });
  }, []);

  const isSelectionAtLimit = selectedJobOrderIds.size >= MAX_SELECTED_JOB_ORDERS;

  const filters = useMemo(
    () => buildApiFiltersFromSearchParams(searchParams, { nowMs: nowMsRef.current }),
    [searchParams]
  );

  const payload = useMemo(
    () => ({
      filters,
      limit: DEFAULT_PAGE_SIZE,
      sortBy,
      cursor: effectiveCursor,
    }),
    [filters, sortBy, effectiveCursor]
  );

  const requestKey = useMemo(
    () => `${filterKey}|${JSON.stringify(effectiveCursor)}|${DEFAULT_PAGE_SIZE}`,
    [filterKey, effectiveCursor]
  );
  const firstPageRequestKey = `${filterKey}|null|${DEFAULT_PAGE_SIZE}`;
  const lastDispatchedRequestKeyRef = useRef(null);
  // Stays set until the first-page reload response arrives, so an in-flight
  // load-more can finish before that reload clears the list.
  const firstPageRefreshPendingRef = useRef(false);
  const wasListLoadingRef = useRef(isLoading);
  useEffect(() => {
    const wasLoading = wasListLoadingRef.current;
    wasListLoadingRef.current = isLoading;
    if (isLoading) return;

    const refreshNotSent =
      firstPageRefreshPendingRef.current &&
      lastDispatchedRequestKeyRef.current !== firstPageRequestKey;

    if (refreshNotSent) {
      lastRequestedCursorKeyRef.current = null;
      lastDispatchedRequestKeyRef.current = firstPageRequestKey;
      if (cursorToFetch !== null) {
        setCursorToFetch(null);
      }
      dispatch(getJobOrdersV2({ ...payload, cursor: null }));
      return;
    }

    if (wasLoading && firstPageRefreshPendingRef.current) {
      firstPageRefreshPendingRef.current = false;
      lastRequestedCursorKeyRef.current = null;
      if (cursorToFetch !== null) {
        setCursorToFetch(null);
        return;
      }
    }

    if (lastDispatchedRequestKeyRef.current !== requestKey) {
      lastDispatchedRequestKeyRef.current = requestKey;
      dispatch(getJobOrdersV2(payload));
    }
  }, [cursorToFetch, dispatch, firstPageRequestKey, isLoading, payload, requestKey]);

  const groupedByStart = useSelector(selectJobOrdersV2GroupedByStart);

  const [flippedStates, setFlippedStates] = useState({});

  const handleFlipClick = (date, cardId) => {
    setFlippedStates((prev) => ({
      ...prev,
      [`${date}_${cardId}`]: !prev[`${date}_${cardId}`],
    }));
  };

  // Local-only reorder so DnD doesn't break; server persistence can come later.
  const [manualOrderByDate, setManualOrderByDate] = useState({});
  const moveCard = (date, fromIndex, toIndex) => {
    if (fromIndex === undefined || toIndex === undefined) return;
    setManualOrderByDate((prev) => {
      const current = prev[date] || groupedByStart?.[date]?.cards?.map((c) => c._id) || [];
      const next = [...current];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return { ...prev, [date]: next };
    });
  };

  const handleLoadMore = useCallback(() => {
    if (firstPageRefreshPendingRef.current) return;
    if (!hasMore) return;
    if (!cursorFromStore) return;
    const nextCursorKey = JSON.stringify(cursorFromStore);
    if (lastRequestedCursorKeyRef.current === nextCursorKey) return;
    lastRequestedCursorKeyRef.current = nextCursorKey;
    setCursorToFetch(cursorFromStore);
  }, [cursorFromStore, hasMore]);

  const sentinelRef = useInfiniteScroll({
    isLoading,
    hasMore,
    onLoadMore: handleLoadMore,
  });

  const onAssociationsCompletedRef = useRef(() => {});
  onAssociationsCompletedRef.current = () => {
    setSelectedJobOrderIds(new Set());
    setIsManageAssociationsModalOpen(false);
    lastRequestedCursorKeyRef.current = null;
    setCursorToFetch(null);
    firstPageRefreshPendingRef.current = true;

    if (isLoading) {
      lastDispatchedRequestKeyRef.current = null;
      return;
    }

    lastDispatchedRequestKeyRef.current = firstPageRequestKey;
    dispatch(getJobOrdersV2({ ...payload, cursor: null }));
  };

  const handleResetFilters = () => {
    setFlippedStates({});
    setManualOrderByDate({});
    setSearchParams(new URLSearchParams());
    lastRequestedCursorKeyRef.current = null;
    setCursorToFetch(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', padding: '24px', gap: '24px' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
          {canCreateJobOrder ? (
            <WaeButton
              variant={BUTTON_VARIANT.DEFAULT}
              actionColor={PRIMARY_COLOR[70]}
              onClick={handleNewJobOrderClick}
              dataTestId="new-job-order-button"
            >
              {LanguageConverterV2('job.create')}
            </WaeButton>
          ) : (
            !isAdmin && <Box sx={{ minWidth: 'fit-content' }} />
          )}
          {isAdmin && (
            <>
              <WaeButton
                variant={BUTTON_VARIANT.DEFAULT}
                actionColor={PRIMARY_COLOR[70]}
                onClick={() => setIsManageAssociationsModalOpen(true)}
                disabled={selectedJobOrderIds.size === 0}
                dataTestId="manage-associations-button"
              >
                {LanguageConverterV2('jobOrders.manageAssociations')}
              </WaeButton>
              <WaeButton
                variant={BUTTON_VARIANT.TEXT}
                onClick={() => setSelectedJobOrderIds(new Set())}
                disabled={selectedJobOrderIds.size === 0}
                dataTestId="clear-selection-button"
                sx={{ color: BLACK[100], minWidth: 'fit-content' }}
              >
                {LanguageConverterV2('jobOrders.clearSelection')}
              </WaeButton>
            </>
          )}
        </Box>
        <JobOrdersFilters
          showAssociatedUserFilter={showCorporationFilter}
          showCorporationFilter={showCorporationFilter}
        />
      </Box>

      {jobOrders.length === 0 && !isLoading ? (
        <NoResultsCard
          headerMsgKey={null}
          bodyMsgKey="jobOrder.search.noResult"
          onResetFilter={handleResetFilters}
        />
      ) : (
        Object.keys(groupedByStart).map((date) => {
          const { weekDay, cards } = groupedByStart[date];
          const manualOrder = manualOrderByDate[date];
          const orderedCards =
            manualOrder && manualOrder.length
              ? manualOrder
                  .map((id) => cards.find((c) => c._id === id))
                  .filter(Boolean)
                  .concat(cards.filter((c) => !manualOrder.includes(c._id)))
              : cards;
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }} key={date}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  height: 40,
                  px: '12px',
                  borderRadius: '8px',
                  backgroundColor: BLACK[10],
                  opacity: 1,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontFamily: PRIMARY_FONT[400],
                    fontSize: '20px',
                    lineHeight: '24px',
                    color: BLACK[100],
                  }}
                >
                  {`${weekDay} -`}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: PRIMARY_FONT[700],
                    fontSize: '20px',
                    lineHeight: '24px',
                    color: BLACK[100],
                  }}
                >
                  {date}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: '25px',
                }}
              >
                {orderedCards.map((card, index) => (
                  <JobCard
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${date}_${card._id}_${index}`}
                    index={index}
                    date={date}
                    moveCard={moveCard}
                    jobOrder={card}
                    isFlipped={flippedStates[`${date}_${card._id}`] || false}
                    handleFlipClick={() => handleFlipClick(date, card._id)}
                    onPlacementCheckStatusUpdate={onPlacementCheckStatusUpdate}
                    clearTimeCardError={clearTimeCardError}
                    handleTimecardEditSubmit={handleTimecardEditSubmit}
                    onStatusUpdate={onStatusUpdate}
                    timecardIsUpdating={timecardIsUpdating}
                    timecardUpdateError={timecardUpdateError}
                    showSelection={isAdmin}
                    checked={selectedJobOrderIds.has(card._id)}
                    disabled={!selectedJobOrderIds.has(card._id) && isSelectionAtLimit}
                    onToggle={() => handleJobOrderSelectionToggle(card._id)}
                    isCancelled={
                      card?.cancelled === true || card?.status === JOB_ORDER_STATUSES.CANCELLED
                    }
                    timeStatus={getTimeStatus(card?.start, card?.end)}
                  />
                ))}
              </Box>
            </Box>
          );
        })
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}

      <div ref={sentinelRef} />

      {canCreateJobOrder && (
        <JobCreateTypeSelectModal
          isOpen={isSelectJobCreateTypeModalOpen}
          onClose={() => setIsSelectJobCreateTypeModalOpen(false)}
        />
      )}
      <ManageAssociationsModal
        isOpen={isManageAssociationsModalOpen}
        jobOrders={jobOrders.filter((jobOrder) => selectedJobOrderIds.has(jobOrder._id))}
        onClose={() => setIsManageAssociationsModalOpen(false)}
        onCompleted={() => onAssociationsCompletedRef.current()}
      />
    </Box>
  );
};

JobOrdersV2.propTypes = {
  flags: PropTypes.shape({}),
};

export default withLDConsumer()(JobOrdersV2);
